import { useLocalSearchParams } from "expo-router";
import { useEffect, useRef, useState } from "react";
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { Badge, Button, Card, Divider, Screen, Text } from "~/components/ui";
import { useRecipeProgress } from "~/hooks/useRecipeProgress";
import {
  parseRecipeSteps,
  useRecipe,
  type RecipeStep,
} from "~/hooks/useRecipes";
import { useRecipeTimer, type RecipeTimer } from "~/hooks/useRecipeTimer";
import { useAuth } from "~/lib/auth";
import { radius, spacing, type ThemeColors } from "~/styles/tokens";
import { useTheme, useThemedStyles } from "~/styles/theme";
import { notify } from "~/utils/notify";

function formatCountdown(ms: number): string {
  if (ms <= 0) return "Ready!";
  const totalSeconds = Math.ceil(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, "0")}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  return `${seconds}s`;
}

function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
}

interface StepCardProps {
  step: RecipeStep;
  stepIndex: number;
  isActive: boolean;
  isCompleted: boolean;
  timer: RecipeTimer | null;
  isStartingTimer: boolean;
  now: number;
  onStart: () => void;
  onCancel: () => void;
  onComplete: () => void;
  onExpand: () => void;
}

function StepCard({
  step,
  stepIndex,
  isActive,
  isCompleted,
  timer,
  isStartingTimer,
  now,
  onStart,
  onCancel,
  onComplete,
  onExpand,
}: StepCardProps) {
  const { colors } = useTheme();
  const styles = useStyles();
  const msLeft = timer
    ? Math.max(0, new Date(timer.notifyAt).getTime() - now)
    : 0;
  const timerReady = timer !== null && msLeft === 0;
  const isWait = step.type === "wait" || step.waitMinutes > 0;
  const cardBg = isCompleted
    ? colors.background
    : isActive
      ? colors.surface
      : colors.surfaceMuted;

  return (
    <TouchableOpacity
      onPress={onExpand}
      activeOpacity={0.85}
      accessibilityRole="button"
      accessibilityLabel={`Step ${stepIndex + 1}: ${step.title}`}
      accessibilityHint="Opens this step and shows actions"
    >
      <View
        style={[
          styles.stepCard,
          { backgroundColor: cardBg },
          isActive && styles.stepCardActive,
        ]}
      >
        <View style={styles.stepRow}>
          <View
            style={[
              styles.stepBadge,
              isCompleted && styles.stepBadgeCompleted,
              isActive && styles.stepBadgeActive,
            ]}
          >
            <Text
              variant="label"
              color={
                isCompleted || isActive
                  ? colors.primaryForeground
                  : colors.muted
              }
              align="center"
            >
              {isCompleted ? "✓" : String(stepIndex + 1)}
            </Text>
          </View>
          <View style={styles.stepContent}>
            <Text
              variant="title"
              color={isCompleted ? colors.muted : colors.foreground}
              style={isCompleted ? styles.strikethrough : undefined}
            >
              {step.title}
            </Text>
            {isWait && !isCompleted ? (
              <Text
                variant="caption"
                color={colors.accent}
                style={{ marginTop: 2 }}
              >
                ⏱ {formatDuration(step.waitMinutes)} wait
              </Text>
            ) : null}
            {timer && !isCompleted ? (
              <View style={styles.countdown}>
                <Text
                  variant="subheading"
                  color={timerReady ? colors.success : colors.muted}
                >
                  {timerReady ? "🎉 Ready!" : `⏳ ${formatCountdown(msLeft)}`}
                </Text>
                {!timerReady ? (
                  <Text variant="caption" color={colors.muted}>
                    Notification queued
                  </Text>
                ) : null}
              </View>
            ) : null}
          </View>
        </View>
        {isActive ? (
          <>
            <Divider style={styles.divider} />
            <Text
              variant="body"
              color={colors.heading}
              style={styles.description}
            >
              {step.description}
            </Text>
            <View style={styles.actions}>
              {isWait && !timer && !isStartingTimer ? (
                <Button
                  label="⏱ Start timer"
                  onPress={onStart}
                  variant="primary"
                  size="sm"
                  style={styles.actionBtn}
                />
              ) : null}
              {isWait && isStartingTimer ? (
                <Button
                  label="Starting…"
                  onPress={onStart}
                  loading
                  variant="primary"
                  size="sm"
                  style={styles.actionBtn}
                />
              ) : null}
              {timer && !timerReady ? (
                <Button
                  label="✕ Cancel timer"
                  onPress={onCancel}
                  variant="ghost"
                  size="sm"
                  style={styles.actionBtn}
                />
              ) : null}
              {!isWait || timerReady || !timer ? (
                <Button
                  label={
                    isWait && !timer ? "Skip timer & continue" : "Mark done ✓"
                  }
                  onPress={onComplete}
                  variant={timerReady ? "primary" : "secondary"}
                  size="sm"
                  style={styles.actionBtn}
                />
              ) : null}
            </View>
          </>
        ) : null}
      </View>
    </TouchableOpacity>
  );
}

export default function RecipeCookScreen() {
  const { colors } = useTheme();
  const styles = useStyles();
  const { id, stepIndex: initialStepIndex } = useLocalSearchParams<{
    id: string;
    stepIndex?: string;
  }>();
  const recipeId = id;
  const { user } = useAuth();
  const { data: recipe, loading, error } = useRecipe(recipeId);
  const {
    startStepTimer,
    cancelTimer,
    getTimerForStep,
    refetch: refetchTimers,
  } = useRecipeTimer(recipeId);
  const progress = useRecipeProgress(recipeId);
  const isAnonymous = !user;

  const [localActiveStep, setLocalActiveStep] = useState<number | null>(
    initialStepIndex != null ? Number(initialStepIndex) || 0 : null,
  );
  const [localCompletedSteps, setLocalCompletedSteps] =
    useState<Set<number> | null>(null);
  const [now, setNow] = useState(0);
  const [startingTimer, setStartingTimer] = useState<number | null>(null);
  const scrollRef = useRef<ScrollView>(null);
  const stepOffsets = useRef<Map<number, number>>(new Map());
  const completedSteps =
    localCompletedSteps ?? new Set(progress.completedSteps);
  const activeStep = localActiveStep ?? progress.activeStep;

  useEffect(() => {
    stepOffsets.current.clear();
  }, [recipeId]);

  useEffect(() => {
    const tick = () => setNow(Date.now());
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const offset = stepOffsets.current.get(activeStep);
      if (offset !== undefined) {
        scrollRef.current?.scrollTo({ y: offset, animated: true });
      }
    }, 200);
    return () => clearTimeout(timeout);
  }, [activeStep]);

  if (loading) {
    return (
      <Screen>
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text variant="caption" style={{ marginTop: spacing.sm }}>
            Loading recipe…
          </Text>
        </View>
      </Screen>
    );
  }

  if (error || !recipe) {
    return (
      <Screen>
        <View style={styles.center}>
          <Text variant="title">😕 Recipe not found</Text>
          <Text variant="caption">{error}</Text>
        </View>
      </Screen>
    );
  }

  const steps = parseRecipeSteps(recipe);
  if (steps.length === 0) {
    return (
      <Screen>
        <View style={styles.center}>
          <Text variant="title">No steps available</Text>
          <Text variant="caption">
            This recipe doesn&apos;t have step-by-step instructions yet.
          </Text>
        </View>
      </Screen>
    );
  }

  const isFinished = completedSteps.size >= steps.length;

  const persistProgress = (nextCompleted: Set<number>, nextActive: number) => {
    void progress.saveProgress({
      completedSteps: [...nextCompleted],
      activeStep: nextActive,
    });
  };

  const handleStartTimer = async (step: RecipeStep, stepIndex: number) => {
    setStartingTimer(stepIndex);
    try {
      await startStepTimer(recipe, step, stepIndex);
    } catch (caught: unknown) {
      notify(
        "Error",
        caught instanceof Error ? caught.message : "Could not start timer",
      );
    } finally {
      setStartingTimer(null);
    }
  };

  const handleCancelTimer = async (timer: RecipeTimer) => {
    try {
      await cancelTimer(timer.id);
      await refetchTimers();
    } catch (caught: unknown) {
      notify(
        "Error",
        caught instanceof Error ? caught.message : "Could not cancel timer",
      );
    }
  };

  const handleComplete = async (stepIndex: number) => {
    const timer = getTimerForStep(stepIndex);
    if (timer) await handleCancelTimer(timer);
    const nextCompleted = new Set([...completedSteps, stepIndex]);
    const nextActive = stepIndex + 1 < steps.length ? stepIndex + 1 : stepIndex;
    setLocalCompletedSteps(nextCompleted);
    if (stepIndex + 1 < steps.length) setLocalActiveStep(nextActive);
    persistProgress(nextCompleted, nextActive);
  };

  return (
    <Screen padded={false}>
      <View style={styles.header}>
        <Text style={styles.headerEmoji}>{recipe.emoji || "🍞"}</Text>
        <View style={styles.headerText}>
          <Text variant="subheading" numberOfLines={1}>
            {recipe.title}
          </Text>
          <Text variant="caption" color={colors.muted}>
            Step {Math.min(activeStep + 1, steps.length)} of {steps.length}
          </Text>
        </View>
        {isFinished ? <Badge label="🎉 Done!" variant="success" /> : null}
      </View>

      <View style={styles.progressTrack}>
        <View
          style={[
            styles.progressFill,
            { width: `${(completedSteps.size / steps.length) * 100}%` },
          ]}
        />
      </View>

      {isAnonymous ? (
        <Card elevation="flat" style={styles.anonBanner}>
          <Text variant="caption" color={colors.heading}>
            🔔 Timers work on this device. Save an account if you want them on
            another phone.
          </Text>
        </Card>
      ) : null}

      <ScrollView
        ref={scrollRef}
        contentContainerStyle={styles.list}
        showsVerticalScrollIndicator={false}
      >
        {steps.map((step, index) => {
          const timer = getTimerForStep(index);
          return (
            <View
              key={step.id}
              onLayout={(event) =>
                stepOffsets.current.set(index, event.nativeEvent.layout.y)
              }
            >
              <StepCard
                step={step}
                stepIndex={index}
                isActive={index === activeStep && !isFinished}
                isCompleted={completedSteps.has(index)}
                timer={startingTimer === index ? null : timer}
                isStartingTimer={startingTimer === index}
                now={now}
                onStart={() => void handleStartTimer(step, index)}
                onCancel={() => {
                  if (timer) void handleCancelTimer(timer);
                }}
                onComplete={() => void handleComplete(index)}
                onExpand={() => {
                  if (!completedSteps.has(index)) {
                    setLocalActiveStep(index);
                    persistProgress(completedSteps, index);
                  }
                }}
              />
            </View>
          );
        })}

        {isFinished ? (
          <Card elevation="raised" style={styles.finishedCard}>
            <Text variant="heading" align="center">
              🎉
            </Text>
            <Text variant="subheading" align="center">
              All done!
            </Text>
            <Text
              variant="body"
              align="center"
              color={colors.heading}
              style={{ marginTop: spacing.xs }}
            >
              Enjoy your {recipe.title} 🍞
            </Text>
          </Card>
        ) : null}

        <View style={{ height: spacing.xxl }} />
      </ScrollView>
    </Screen>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      gap: spacing.sm,
      padding: spacing.xl,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.md,
      backgroundColor: c.background,
      gap: spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: c.border,
    },
    headerEmoji: {
      fontSize: 32,
      lineHeight: 38,
    },
    headerText: {
      flex: 1,
    },
    progressTrack: {
      marginHorizontal: spacing.md,
      borderRadius: 2,
      height: 4,
      backgroundColor: c.surfaceMuted,
    },
    progressFill: {
      height: 4,
      backgroundColor: c.accent,
      borderRadius: 2,
    },
    anonBanner: {
      marginHorizontal: spacing.md,
      marginTop: spacing.md,
      borderColor: c.accent,
    },
    list: {
      padding: spacing.md,
      gap: spacing.sm,
    },
    stepCard: {
      borderRadius: radius.xl,
      padding: spacing.md,
      marginBottom: spacing.xs,
      borderWidth: 1,
      borderColor: c.border,
    },
    stepCardActive: {
      borderColor: c.accent,
      borderWidth: 1.5,
    },
    stepRow: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: spacing.sm,
    },
    stepBadge: {
      width: 28,
      height: 28,
      borderRadius: radius.full,
      backgroundColor: c.surfaceMuted,
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      marginTop: 2,
    },
    stepBadgeActive: {
      backgroundColor: c.primary,
    },
    stepBadgeCompleted: {
      backgroundColor: c.success,
    },
    stepContent: {
      flex: 1,
    },
    strikethrough: {
      textDecorationLine: "line-through",
    },
    countdown: {
      marginTop: spacing.xs,
    },
    divider: {
      marginVertical: spacing.sm,
    },
    description: {
      marginBottom: spacing.sm,
    },
    actions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.xs,
      marginTop: spacing.xs,
    },
    actionBtn: {
      flexShrink: 1,
    },
    finishedCard: {
      marginTop: spacing.md,
      padding: spacing.xl,
      alignItems: "center",
      gap: spacing.xs,
    },
  });
